import React from "react";
import { formatMessage } from "src/helpers/format-message";
import { CommentDialogState } from "../types";

interface CommentDialogProps {
  commentDialog: CommentDialogState;
  setCommentDialog: React.Dispatch<React.SetStateAction<CommentDialogState>>;
  handleAddComment: () => void;
  handleSubmitFinalComment: () => void;
}

export const CommentDialog: React.FC<CommentDialogProps> = ({
  commentDialog,
  setCommentDialog,
  handleAddComment,
  handleSubmitFinalComment,
}) => {
  if (!commentDialog.isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-xl font-bold mb-4">Add Comment</h2>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Original Query
          </label>
          <div className="p-3 bg-gray-100 rounded-lg">
            {commentDialog.originalMessage}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Original Response
          </label>
          <div
            className="p-3 bg-gray-100 rounded-lg text-left"
            dangerouslySetInnerHTML={{
              __html: commentDialog.botResponse,
            }}
          ></div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Comment
          </label>
          <textarea
            className="w-full p-3 border rounded-lg"
            rows={4}
            value={commentDialog.comment}
            onChange={(e) =>
              setCommentDialog((prev) => ({
                ...prev,
                comment: e.target.value,
              }))
            }
            placeholder="Enter your comment..."
          />
        </div>

        {commentDialog.suggestedResponse && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Suggested New Response
            </label>
            <div
              className="p-3 bg-gray-100 rounded-lg text-left prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{
                __html: formatMessage(commentDialog.suggestedResponse || ""),
              }}
            ></div>

            <div className="flex gap-2 mt-4">
              <button
                className="flex-1 bg-primary text-white py-2 px-4 rounded hover:bg-primary/80"
                onClick={handleSubmitFinalComment}
                disabled={commentDialog.isLoading}
              >
                {commentDialog.isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-2"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing comment...
                  </div>
                ) : (
                  "Approve"
                )}
              </button>
              <button
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
                onClick={() =>
                  setCommentDialog((prev) => ({
                    ...prev,
                    suggestedResponse: null,
                    comment: "",
                    isLoading: false,
                  }))
                }
              >
                Reject
              </button>
            </div>
          </div>
        )}

        {!commentDialog.suggestedResponse && (
          <div className="flex gap-2">
            <button
              className="flex-1 bg-primary text-white py-2 px-4 rounded hover:bg-primary/80 disabled:opacity-50"
              onClick={handleAddComment}
              disabled={!commentDialog.comment || commentDialog.isLoading}
            >
              {commentDialog.isLoading ? "Thinking..." : "Try comment"}
            </button>
            <button
              className="flex-1 bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
              onClick={() =>
                setCommentDialog((prev) => ({ ...prev, isOpen: false }))
              }
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
